import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  List,
  ListItem,
  Divider,
  CircularProgress,
  IconButton,
  Fade,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  People as PeopleIcon,
  Tv as TvIcon,
  HourglassEmpty as PendingIcon,
  Assessment as ReportsIcon,
  Login as LoginIcon,
  PersonAdd as PersonAddIcon,
  Devices as DevicesIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI } from '../services/api';
import { toast } from 'react-toastify';

const DashboardNew = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [stats, setStats] = useState({
    totalUsers: 0,
    totalScreens: 0,
    pendingApprovals: 0,
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async (showRefresh = false) => {
    try {
      if (showRefresh) setRefreshing(true);

      const [statsResponse, activityResponse] = await Promise.all([
        dashboardAPI.getStats().catch((err) => {
          console.error('Stats API Error:', err.response?.data || err.message);
          return { data: { stats: { totalUsers: 0, totalScreens: 0, pendingApprovals: 0 } } };
        }),
        dashboardAPI.getRecentActivity(10).catch((err) => {
          console.error('Activity API Error:', err.response?.data || err.message);
          return { data: { activities: [] } };
        }),
      ]);

      if (statsResponse.data && statsResponse.data.stats) {
        setStats({
          totalUsers: statsResponse.data.stats.totalUsers || 0,
          totalScreens: statsResponse.data.stats.totalScreens || 0,
          pendingApprovals: statsResponse.data.stats.pendingApprovals || 0,
        });
      }

      if (activityResponse.data && activityResponse.data.activities) {
        setRecentActivity(activityResponse.data.activities);
      }

      if (showRefresh) {
        toast.success('Dashboard refreshed');
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    fetchDashboardData(true);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <CircularProgress size={60} color="primary" />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      {/* Header with Title and Actions */}
      <Fade in={true} timeout={500}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Box>
            <Typography
              variant={isMobile ? 'h5' : 'h4'}
              sx={{
                fontWeight: 700,
                color: '#1e293b',
                mb: 0.5,
              }}
            >
              Dashboard Overview
            </Typography>
            <Typography variant="body2" color="#64748b" sx={{ fontSize: '0.9rem' }}>
              Real-time system status
            </Typography>
          </Box>
          <IconButton
            onClick={handleRefresh}
            disabled={refreshing}
            sx={{
              backgroundColor: '#7C3AED',
              color: '#ffffff',
              borderRadius: '8px',
              p: 1.25,
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: '#6D28D9',
                transform: 'rotate(180deg)',
              },
              '&:disabled': {
                backgroundColor: '#cbd5e1',
              },
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Box>
      </Fade>

      {/* Quick Stats - Grid Cards */}
      <Fade in={true} timeout={700}>
        <Grid container spacing={isMobile ? 2 : 3} sx={{ mb: 3 }}>
          {/* Users Card */}
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                height: '100%',
                borderTop: '4px solid #7C3AED',
                backgroundColor: '#ffffff',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 32px rgba(124, 58, 237, 0.15)',
                },
              }}
            >
              <CardContent sx={{ pb: '20px !important', pt: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#64748b',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        fontSize: '0.75rem',
                        mb: 1,
                      }}
                    >
                      Total Users
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '1.75rem',
                        fontWeight: 800,
                        color: '#1e293b',
                        lineHeight: 1,
                      }}
                    >
                      {stats.totalUsers}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      background: '#7C3AED15',
                      color: '#7C3AED',
                      borderRadius: '12px',
                      width: 56,
                      height: 56,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <PeopleIcon sx={{ fontSize: 28 }} />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Screens Card */}
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                height: '100%',
                borderTop: '4px solid #0F172A',
                backgroundColor: '#ffffff',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 32px rgba(0, 0, 0, 0.1)',
                },
              }}
            >
              <CardContent sx={{ pb: '20px !important', pt: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#64748b',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        fontSize: '0.75rem',
                        mb: 1,
                      }}
                    >
                      Total Screens
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '1.75rem',
                        fontWeight: 800,
                        color: '#1e293b',
                        lineHeight: 1,
                      }}
                    >
                      {stats.totalScreens}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      background: '#0F172A15',
                      color: '#0F172A',
                      borderRadius: '12px',
                      width: 56,
                      height: 56,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <TvIcon sx={{ fontSize: 28 }} />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Pending Approvals Card */}
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                height: '100%',
                borderTop: '4px solid #EA580C',
                backgroundColor: '#ffffff',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 32px rgba(234, 88, 12, 0.15)',
                },
              }}
            >
              <CardContent sx={{ pb: '20px !important', pt: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#64748b',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        fontSize: '0.75rem',
                        mb: 1,
                      }}
                    >
                      Pending Approvals
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '1.75rem',
                        fontWeight: 800,
                        color: stats.pendingApprovals > 0 ? '#EA580C' : '#1e293b',
                        lineHeight: 1,
                      }}
                    >
                      {stats.pendingApprovals}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      background: '#EA580C15',
                      color: '#EA580C',
                      borderRadius: '12px',
                      width: 56,
                      height: 56,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.3s ease',
                      animation: stats.pendingApprovals > 0 ? 'pulse 2s infinite' : 'none',
                      '@keyframes pulse': {
                        '0%, 100%': { opacity: 1 },
                        '50%': { opacity: 0.7 },
                      },
                    }}
                  >
                    <PendingIcon sx={{ fontSize: 28 }} />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Fade>

      {/* Actions */}
      <Fade in={true} timeout={900}>
        <Paper
          sx={{
            p: isMobile ? 2 : 3,
            mb: 3,
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              mb: 2,
              color: '#1e293b',
              fontSize: '0.9rem',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Quick Actions
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1.5,
            }}
          >
            <Button
              variant="contained"
              startIcon={<DevicesIcon />}
              onClick={() => navigate('/screens')}
              size={isMobile ? 'medium' : 'large'}
              sx={{
                flex: isMobile ? '1 1 100%' : '0 1 auto',
                minWidth: isMobile ? 'auto' : 140,
                fontWeight: 600,
                backgroundColor: '#7C3AED',
                '&:hover': {
                  backgroundColor: '#6D28D9',
                },
              }}
            >
              Add Screen
            </Button>
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => navigate('/users')}
              size={isMobile ? 'medium' : 'large'}
              sx={{
                flex: isMobile ? '1 1 100%' : '0 1 auto',
                minWidth: isMobile ? 'auto' : 140,
                fontWeight: 600,
                backgroundColor: '#0F172A',
                '&:hover': {
                  backgroundColor: '#1e293b',
                },
              }}
            >
              Add User
            </Button>
            <Button
              variant="outlined"
              startIcon={<ReportsIcon />}
              onClick={() => navigate('/activity-logs')}
              size={isMobile ? 'medium' : 'large'}
              sx={{
                flex: isMobile ? '1 1 100%' : '0 1 auto',
                minWidth: isMobile ? 'auto' : 140,
                fontWeight: 600,
                borderColor: '#cbd5e1',
                color: '#64748b',
                '&:hover': {
                  borderColor: '#7C3AED',
                  color: '#7C3AED',
                  backgroundColor: 'rgba(124, 58, 237, 0.05)',
                },
              }}
            >
              View Logs
            </Button>
          </Box>
        </Paper>
      </Fade>

      {/* Recent Activity */}
      <Fade in={true} timeout={1100}>
        <Paper
          sx={{
            p: isMobile ? 2 : 3,
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
          }}
        >
          <Box sx={{ pb: 2, borderBottom: '1px solid #e2e8f0', mb: 2 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                color: '#1e293b',
                fontSize: '0.9rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Recent Activity
            </Typography>
          </Box>
          {recentActivity.length > 0 ? (
            <List sx={{ p: 0 }}>
              {recentActivity.map((activity, index) => {
                const getActivityIcon = () => {
                  if (activity.action?.includes('User') || activity.action?.includes('user')) {
                    return <PersonAddIcon sx={{ fontSize: 20, color: '#7C3AED' }} />;
                  } else if (activity.action?.includes('Screen') || activity.action?.includes('screen')) {
                    return <DevicesIcon sx={{ fontSize: 20, color: '#0F172A' }} />;
                  } else if (activity.action?.includes('login') || activity.action?.includes('Login')) {
                    return <LoginIcon sx={{ fontSize: 20, color: '#22c55e' }} />;
                  }
                  return <LoginIcon sx={{ fontSize: 20, color: '#94a3b8' }} />;
                };

                return (
                  <React.Fragment key={activity._id || index}>
                    <ListItem
                      sx={{
                        px: 0,
                        py: 1.5,
                        display: 'flex',
                        gap: 1.5,
                        transition: 'all 0.2s ease',
                        borderRadius: 1,
                        '&:hover': {
                          backgroundColor: '#f8f9fb',
                          pl: 1,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: 32,
                          mt: 0.5,
                        }}
                      >
                        {getActivityIcon()}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            color: '#1e293b',
                            mb: 0.25,
                          }}
                        >
                          {activity.action || 'Activity performed'}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: '#94a3b8', fontSize: '0.75rem' }}
                        >
                          {activity.createdAt || activity.timestamp
                            ? new Date(activity.createdAt || activity.timestamp).toLocaleString()
                            : 'Recently'}
                        </Typography>
                      </Box>
                    </ListItem>
                    {index < recentActivity.length - 1 && (
                      <Divider sx={{ borderColor: '#e2e8f0', my: 0 }} />
                    )}
                  </React.Fragment>
                );
              })}
            </List>
          ) : (
            <Box
              sx={{
                textAlign: 'center',
                py: 4,
                px: 2,
                backgroundColor: '#f8f9fb',
                borderRadius: '8px',
                border: '1px dashed #cbd5e1',
              }}
            >
              <LoginIcon
                sx={{
                  fontSize: '2rem',
                  color: '#cbd5e1',
                  mb: 1,
                }}
              />
              <Typography color="#64748b" variant="body2" sx={{ fontWeight: 500 }}>
                No recent activity yet
              </Typography>
              <Typography
                color="#94a3b8"
                variant="caption"
                sx={{ mt: 0.5, display: 'block', fontSize: '0.8rem' }}
              >
                Activity will appear here as you manage the system
              </Typography>
            </Box>
          )}
        </Paper>
      </Fade>
    </Box>
  );
};

export default DashboardNew;